import { Directive, Input, OnInit, OnDestroy, ElementRef, Output, EventEmitter, OnChanges, SimpleChanges, inject } from '@angular/core';
import { Hotspot } from 'marzipano';
import { ISphericalCoords } from '@newroom-connect/library/interfaces';
import { LoggingService } from '@newroom-connect/library/services';
import { BoundingBox } from '@newroom-connect/library/types';

import { MarzipanoScene } from '../models/marzipano-scene.model';
import { MarzipanoLayerIndex } from '../services/marzipano-layer.service';
import { TransformOverlay, IBoundingBoxEvent } from '../overlays/transform-overlay';
import { DragOverlay, IDragMoveEvent } from '../overlays/drag-overlay';
import { ISceneHostpotContainer } from '../models/marzipano-scene-hotspot.model';
import { IMarzipanoPerspective } from '../interfaces/marzipano-scene.interface';

/**
 * Configuration for creating a hotspot.
 */
export interface ISceneHotspotConfig {
  id: string;
  position: ISphericalCoords;
  perspective?: IMarzipanoPerspective;
  visible?: boolean;
}

/**
 * Scene Hotspot Directive for Marzipano 360-degree scenes.
 *
 * Features:
 * - Positioning using spherical coordinates (yaw, pitch).
 * - Overlay support for applying free transform and perspective adjustments to the hotspot.
 * - Lifecycle management with async initialization.
 * - Camera animation to focus on hotspot when selected in edit mode.
 *
 * @example
 * ```html
 * <div
 *   nrcSceneHotspot
 *   [scene]="marzipanoScene"
 *   [areaElement]="areaElement"
 *   [isTransformable]="true"
 *   [isDraggable]="true"
 *   [enableCameraAnimation]="true"
 *   [cameraAnimationDuration]="1500"
 *   [cameraAnimationTrigger]="'click'"
 *   (hotspotCreated)="onHotspotCreated($event)"
 *   (hotspotError)="onHotspotError($event)"
 *   (boundingBoxChanged)="onBoundingBoxChanged($event)"
 *   (cameraAnimated)="onCameraAnimated($event)"
 * >
 *   <div class="hotspot-content">
 *     Your hotspot content here
 *   </div>
 * </div>
 * ```
 *
 */
@Directive({
  selector: '[nrcSceneHotspot]',
  standalone: true
})
export class SceneHotspotDirective implements OnInit, OnChanges, OnDestroy {
  private readonly logger = inject(LoggingService);

  @Input({ required: true }) public hotspotId!: string;
  @Input({ required: true }) public boundingBox!: BoundingBox;

  @Input() public scene: MarzipanoScene | null = null;
  @Input() public zIndex = 10;
  @Input() public parentContainer?: ElementRef<HTMLDivElement>;

  @Input() public isVisible = true;
  @Input() public isTransformable = false;
  @Input() public isDraggable = false;
  @Input() public isNew = false;

  // Camera animation properties
  @Input() public enableCameraAnimation = true;
  @Input() public cameraAnimationDuration = 1000;
  @Input() public cameraAnimationTrigger: 'click' | 'doubleClick' | 'selection' = 'click';

  @Output() public hotspotCreated = new EventEmitter<Hotspot>();
  @Output() public hotspotError = new EventEmitter<Error>();
  @Output() public boundingBoxChanged = new EventEmitter<IBoundingBoxEvent>();

  @Output() public hotspotClick = new EventEmitter<{ id: string; event: MouseEvent }>();
  @Output() public hotspotDoubleClick = new EventEmitter<{ id: string; event: MouseEvent }>();
  @Output() public hotspotClickOutside = new EventEmitter<{ id: string; event: MouseEvent }>();

  @Output() public cameraAnimated = new EventEmitter<{ yaw: number; pitch: number; fov?: number }>();

  private element: HTMLElement;

  private isInitialized = false;

  // Simplified overlay management
  private transformOverlay = new TransformOverlay();
  private dragOverlay = new DragOverlay();

  // Scene hotspot container for managing the hotspot, drag overlay, and transform overlay hotspots.
  private sceneHotspotContainer: ISceneHostpotContainer | null = null;

  /**
   *
   * @param elementRef - Element reference for the directive.
   */
  constructor(elementRef: ElementRef) {
    this.element = elementRef.nativeElement;
  }

  /**
   * Initialize the directive and create the hotspot when the scene is ready.
   */
  public async ngOnInit(): Promise<void> {
    try {
      // Wait for scene to be ready before creating hotspot
      const createdPosition = await this.createHotspot();

      this.isInitialized = true;

      // Apply initial layer assignment after hotspot is fully created and initialized
      setTimeout(() => {
        this.updateLayerAssignment();
      }, 0);

      if (this.isNew && createdPosition && this.boundingBox.spherical?.yaw !== createdPosition.yaw && this.boundingBox.spherical?.pitch !== createdPosition.pitch) {
        // Update the bounding box spherical coordinates and emit the change
        this.updateBoundingBoxWithNewPosition(createdPosition.yaw, createdPosition.pitch);
      }
    } catch (error) {
      this.logger.error('Failed to initialize SceneHotspot directive', error as Error);
      this.hotspotError.emit(error as Error);
    }
  }

  /**
   * Handle input changes, particularly areaElement updates.
   *
   * @param changes - Angular SimpleChanges object.
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.isInitialized) {
      return;
    }

    // Handle areaElement changes (update in place, do not recreate)
    if (changes['boundingBox'] && !changes['boundingBox'].firstChange) {
      if (!this.isInitialized) {
        this.createHotspot();
      } else if (this.boundingBox) {
        this.updateHotspotFromBoundingBox(this.boundingBox);
      }
    }

    // Handle scene changes
    if (changes['scene'] && !changes['scene'].firstChange) {
      // Recreate hotspot with new scene
      this.createHotspot();
    }

    // Handle overlay visibility changes
    if (changes['isTransformable'] && this.isInitialized) {
      this.updateTransformOverlayVisibility();
    }

    if (changes['isDraggable'] && this.isInitialized) {
      this.updateDragOverlayVisibility();
    }

    // Handle zIndex changes by directly updating layer assignment
    if (changes['zIndex'] && !changes['zIndex'].firstChange) {
      this.logger.debug(`zIndex changed for hotspot ${this.hotspotId}: ${changes['zIndex'].previousValue} -> ${changes['zIndex'].currentValue}`);
      this.updateLayerAssignment();
    }
  }

  /**
   * Clean up the hotspot when the directive is destroyed.
   */
  public ngOnDestroy(): void {
    this.removeHotspot();
    this.transformOverlay.destroy();
    this.dragOverlay.destroy();
    this.isInitialized = false;
  }

  /**
   * Manually trigger camera animation to focus on this hotspot.
   * This method can be called programmatically to animate the camera,
   * regardless of the cameraAnimationTrigger setting.
   *
   * @param customDuration - Optional custom animation duration in milliseconds.
   * @returns Promise that resolves when animation completes.
   */
  public async focusCamera(customDuration?: number): Promise<void> {
    if (!this.scene || !this.boundingBox) {
      return;
    }

    try {
      const position = this.getBoundingBoxSphericalPosition(this.boundingBox);
      const cameraState = this.scene.getCurrentCameraState();
      const duration = customDuration ?? this.cameraAnimationDuration;

      await this.scene.animateToViewState({
        yaw: position.yaw,
        pitch: position.pitch,
        fov: cameraState.fov
      }, duration);

      this.cameraAnimated.emit({
        yaw: position.yaw,
        pitch: position.pitch,
        fov: cameraState.fov
      });
    } catch (error) {
      this.logger.error('Manual camera focus failed', error as Error);
      throw error;
    }
  }

  /**
   * Create the hotspot using Marzipano's native API.
   * This follows the simplified approach with overlay delegation.
   *
   * @returns
   */
  private async createHotspot(): Promise<ISphericalCoords | null> {
    if (!this.scene || !this.scene.marzipanoScene || !this.boundingBox) {
      this.logger.error('Scene or marzipanoScene is not available');
      return null;
    }

    try {
      // Remove existing hotspot if it exists
      this.removeHotspot();

      // Apply basic width and height sizing based on bounding box and parent container (before config)
      this.applyBasicSizing();
      // Ensure consistent transform origin for matrix math (before config)
      this.element.style.transformOrigin = '50% 50%';

      // Convert area element to hotspot config (now that element has correct size/origin)
      const hotspotConfig = this.convertBoundingBoxToHotspotConfig(this.hotspotId, this.boundingBox);
      const viewerElement = this.scene.marzipanoViewer?.domElement();

      if (!hotspotConfig || !viewerElement) {
        return null;
      }

      // Create position object for Marzipano
      const position = { yaw: hotspotConfig.position.yaw, pitch: hotspotConfig.position.pitch };

      // Create perspective configuration base
      const perspective: IMarzipanoPerspective = {
        radius: hotspotConfig.perspective?.radius || this.scene.calculateDefaultRadius(),
        extraTransforms: hotspotConfig.perspective?.extraTransforms || 'translate(-50%, -50%)'
      };

      // Mark element as an NRC hotspot for outside-click detection
      this.element.setAttribute('data-nrc-hotspot', 'true');

      // Create overlays using the custom overlay classes (must happen before computing matrix from corners)
      const overlays = this.createOverlays(this.hotspotId, this.boundingBox, viewerElement);

      if (!overlays) {
        return null;
      }
      // Create hotspot via manager so it can auto-attach overlay etc.
      this.sceneHotspotContainer = this.scene.hotspotManager.addHotspot({
        id: this.hotspotId,
        position: { yaw: position.yaw, pitch: position.pitch },
        perspective,
        element: this.element,
        visible: this.isVisible
      }, overlays?.dragOverlayElement, overlays?.transformOverlayElement);

      this.hotspotCreated.emit(this.sceneHotspotContainer.hotspot);

      // After hotspot is attached to Marzipano DOM, ensure overlay sizes match the actual element
      const width = Math.max(0, this.element.offsetWidth);
      const height = Math.max(0, this.element.offsetHeight);

      this.dragOverlay.updateSize(width, height);
      this.transformOverlay.updateSize(width, height);

      // If saved corners exist, recompute matrix now that element is in final DOM context
      if (this.boundingBox.perspective?.cornerPositions) {
        const recomputed = this.transformOverlay.applySavedCornersTransform(this.boundingBox.perspective.cornerPositions);

        if (recomputed?.perspective?.transformMatrix) {
          const extraTransforms = `translate(-50%, -50%) ${recomputed.perspective.transformMatrix}`;
          const newRadius = this.boundingBox.spherical?.distance || this.scene.calculateDefaultRadius();

          // Push the transform to Marzipano
          this.scene.hotspotManager.updateHotspotTransform(this.hotspotId, { radius: newRadius, extraTransforms }, position);

          // Merge back recomputed perspective into our local bounding box
          this.boundingBox = {
            ...this.boundingBox,
            perspective: {
              ...this.boundingBox.perspective,
              transformMatrix: recomputed.perspective.transformMatrix
            }
          };
        }
      }

      // Set initial visibility
      this.updateTransformOverlayVisibility();
      this.updateDragOverlayVisibility();

      return position;
    } catch (error) {
      this.logger.error('Failed to create hotspot', error as Error);
      this.hotspotError.emit(error as Error);
      return null;
    }
  }

  /**
   * Initialize overlays using the new overlay classes.
   *
   * @param id
   * @param boundingBox
   * @param viewerElement
   * @returns The created overlay elements.
   */
  private createOverlays(id: string, boundingBox: BoundingBox, viewerElement: HTMLElement): {transformOverlayElement: HTMLElement, dragOverlayElement: HTMLElement} | null {
    if (!this.scene) {
      return null;
    }

    const transformOverlayElement = this.transformOverlay.createTransformOverlayComponent(
      id,
      boundingBox,
      this.element,
      (event: IBoundingBoxEvent) => {
        // Use centralized merge strategy for transform updates
        this.handleBoundingBoxUpdate(event.boundingBox, 'transform');
      },
      () => {
        this.scene?.viewManager.disableControls();
      },
      () => {
        this.scene?.viewManager.enableControls();
      }
    );

    // Create drag overlay with camera animation integration
    const dragOverlayElement = this.dragOverlay.createDragOverlayComponent(
      id,
      boundingBox,
      this.element,
      viewerElement,
      this.scene,
      {
        onDragMove: (event: IDragMoveEvent) => {
          // Use centralized merge strategy for drag updates
          const updatedBoundingBox: BoundingBox = {
            ...this.boundingBox,
            spherical: {
              ...this.boundingBox.spherical,
              yaw: event.position.yaw,
              pitch: event.position.pitch
            }
          };

          this.handleBoundingBoxUpdate(updatedBoundingBox, 'drag');
        },
        onClick: (payload: { id: string; event: MouseEvent }) => {
          this.hotspotClick.emit(payload);
          // Trigger camera animation on click if configured
          if (this.cameraAnimationTrigger === 'click') {
            this.animateCameraToHotspot();
          }
        },
        onDoubleClick: (payload: { id: string; event: MouseEvent }) => {
          this.hotspotDoubleClick.emit(payload);
          // Trigger camera animation on double-click if configured
          if (this.cameraAnimationTrigger === 'doubleClick') {
            this.animateCameraToHotspot();
          }
        },
        onClickOutside: (payload: { id: string; event: MouseEvent }) => this.hotspotClickOutside.emit(payload),
        onDragStart: () => {
          // Disable camera controls when dragging starts
          this.scene?.viewManager.disableControls();
        },
        onDragEnd: () => {
          // Re-enable camera controls when dragging ends
          this.scene?.viewManager.enableControls();
        }
      }
    );

    return {
      transformOverlayElement,
      dragOverlayElement
    };
  }

  /**
   * Centralized bounding box update handler that properly merges updates from different sources.
   * This prevents drag and transform operations from overwriting each other's data.
   *
   * @param newBoundingBox - The new bounding box data.
   * @param source - The source of the update ('drag' or 'transform').
   */
  private handleBoundingBoxUpdate(newBoundingBox: BoundingBox, source: 'drag' | 'transform'): void {
    if (!this.boundingBox) {
      return;
    }

    // Merge the new bounding box with existing data, preserving all properties
    const mergedBoundingBox: BoundingBox = this.mergeBoundingBoxUpdate(this.boundingBox, newBoundingBox, source);

    // Update internal state
    this.boundingBox = mergedBoundingBox;

    // Update the hotspot position and transform
    this.updateHotspotFromBoundingBox(mergedBoundingBox);

    // Emit the merged bounding box to parent components
    this.boundingBoxChanged.emit({
      hotspotId: this.hotspotId,
      boundingBox: mergedBoundingBox
    });
  }

  /**
   * Intelligently merge bounding box updates to preserve data from both drag and transform operations.
   *
   * @param currentBoundingBox - The current bounding box state.
   * @param newBoundingBox - The new bounding box data.
   * @param source
   * @returns The merged bounding box.
   */
  private mergeBoundingBoxUpdate(currentBoundingBox: BoundingBox, newBoundingBox: BoundingBox, source: 'drag' | 'transform'): BoundingBox {
    const merged: BoundingBox = {
      // Preserve core 2D properties
      width: newBoundingBox.width ?? currentBoundingBox.width,
      height: newBoundingBox.height ?? currentBoundingBox.height,
      top: newBoundingBox.top ?? currentBoundingBox.top,
      left: newBoundingBox.left ?? currentBoundingBox.left,

      // Merge spherical data (prioritize new data for drag operations)
      spherical: {
        yaw: newBoundingBox.spherical?.yaw ?? currentBoundingBox.spherical?.yaw ?? 0,
        pitch: newBoundingBox.spherical?.pitch ?? currentBoundingBox.spherical?.pitch ?? 0,
        distance: newBoundingBox.spherical?.distance ?? currentBoundingBox.spherical?.distance
      },

      // Merge perspective data (prioritize new data for transform operations)
      perspective: {
        // Always preserve existing perspective data
        ...currentBoundingBox.perspective,
        // Apply new perspective data if provided
        ...newBoundingBox.perspective,
        // For transform operations, ensure we don't lose corner positions
        ...(source === 'transform' && newBoundingBox.perspective ? {
          transformMatrix: newBoundingBox.perspective.transformMatrix,
          cornerPositions: newBoundingBox.perspective.cornerPositions ?? currentBoundingBox.perspective?.cornerPositions,
          cornerPositionsNormalized: newBoundingBox.perspective.cornerPositionsNormalized ?? currentBoundingBox.perspective?.cornerPositionsNormalized,
          referenceSize: newBoundingBox.perspective.referenceSize ?? currentBoundingBox.perspective?.referenceSize
        } : {})
      }
    };

    return merged;
  }

  /**
   * Update transform overlay visibility based on isTransformable state.
   * Controls both the Angular component (corner dots) and the Marzipano hotspot visibility.
   */
  private updateTransformOverlayVisibility(): void {
    try {
      if (this.isTransformable) {
        this.logger.debug(`Showing transform overlay for hotspot ${this.hotspotId}`);
        // Show the Angular component (corner dots)
        this.transformOverlay.show();

        // Show the Marzipano transform overlay hotspot
        if (this.sceneHotspotContainer?.transformOverlayHotspot) {
          this.sceneHotspotContainer.transformOverlayHotspot.show();
        }
      } else {
        this.logger.debug(`Hiding transform overlay for hotspot ${this.hotspotId}`);
        // Hide the Angular component (corner dots)
        this.transformOverlay.hide();

        // Hide the Marzipano transform overlay hotspot
        if (this.sceneHotspotContainer?.transformOverlayHotspot) {
          this.sceneHotspotContainer.transformOverlayHotspot.domElement().style.display = 'none';
        }
      }
    } catch (error) {
      this.logger.error('Failed to update transform overlay visibility', error as Error);
    }
  }

  /**
   * Update drag overlay visibility and draggable state.
   * Includes debugging to track state changes.
   */
  private updateDragOverlayVisibility(): void {
    try {
      // Keep drag overlay visible always; toggle draggability only
      this.logger.debug(`Setting drag overlay draggable=${this.isDraggable} for hotspot ${this.hotspotId}`);
      this.dragOverlay.setDraggable(this.isDraggable);
      this.dragOverlay.show();
    } catch (error) {
      this.logger.error('Failed to update drag overlay visibility', error as Error);
    }
  }

  /**
   * Update hotspot and overlay in place from the current boundingBox values.
   * Skips updates during active transform operations to prevent interference.
   *
   * @param boundingBox
   */
  private updateHotspotFromBoundingBox(boundingBox: BoundingBox): void {
    if (!this.sceneHotspotContainer?.hotspot || !this.boundingBox || !this.scene) {
      return;
    }

    try {
      // 1) Determine the position (yaw/pitch).
      const elementPosition = this.getBoundingBoxSphericalPosition(boundingBox);

      // 2) Determine the perspective radius and transform matrix
      const newRadius = boundingBox.spherical?.distance || this.scene.calculateDefaultRadius();
      let extraTransforms = 'translate(-50%, -50%)';

      // Prefer recomputing from saved corners for current size
      const initialCorners = boundingBox.perspective?.cornerPositions;

      if (initialCorners) {
        const recomputed = this.transformOverlay.applySavedCornersTransform(initialCorners);
        const matrix = recomputed?.perspective?.transformMatrix ?? boundingBox.perspective?.transformMatrix;

        if (matrix && matrix.trim() !== '') {
          extraTransforms = `${extraTransforms} ${matrix}`;
        }
      } else {
        // Fallback to saved matrix only
        const savedMatrix = boundingBox.perspective?.transformMatrix ?? '';

        if (savedMatrix && savedMatrix.trim() !== '') {
          extraTransforms = `${extraTransforms} ${savedMatrix}`;
        }
      }

      // Update hotspot transform via manager
      this.scene.hotspotManager.updateHotspotTransform(
        this.hotspotId,
        { radius: newRadius, extraTransforms },
        elementPosition
      );

      // 4) Update visibility
      if (this.isVisible) {
        this.sceneHotspotContainer.hotspot.show();
      } else {
        this.sceneHotspotContainer.hotspot.domElement().style.display = 'none';
      }

      this.logger.debug('Hotspot updated in place from areaElement');
    } catch (error) {
      this.logger.warn('Failed to update hotspot in place; consider recreate', error as Error);
    }
  }

  /**
   * Remove the existing hotspot from the scene.
   */
  private removeHotspot(): void {
    if (!this.sceneHotspotContainer?.hotspot || !this.scene || !this.boundingBox) {
      return;
    }

    try {
      // Destroy overlays first
      this.transformOverlay.destroy();
      this.dragOverlay.destroy();

      // Remove hotspot
      this.scene.hotspotManager.removeHotspotById(this.hotspotId);
      this.sceneHotspotContainer = null;
    } catch (error) {
      this.logger.warn('Failed to remove hotspot', error as Error);
    }
  }

  /**
   * Convert an area element to a scene hotspot configuration.
   * This allows using the new simplified SceneHotspotDirective with area elements.
   *
   * @param hotspotId
   * @param boundingBox
   *
   * @returns Hotspot configuration.
   */
  private convertBoundingBoxToHotspotConfig(hotspotId: string, boundingBox: BoundingBox): ISceneHotspotConfig | null {
    if (!this.scene) {
      return null;
    }

    const config: ISceneHotspotConfig = {
      id: hotspotId,
      position: this.getBoundingBoxSphericalPosition(boundingBox),
      visible: this.isVisible
    };

    const savedMatrix = boundingBox.perspective?.transformMatrix ?? '';

    // Try to compute a size-correct matrix up front using current element size and saved corners.
    // Falls back to savedMatrix if no corners available.
    let initialExtraTransforms = 'translate(-50%, -50%)';

    if (savedMatrix && savedMatrix.trim() !== '') {
      initialExtraTransforms = `${initialExtraTransforms} ${savedMatrix.trim()}`;
    }

    config.perspective = {
      radius: boundingBox.spherical?.distance ?? this.scene.calculateDefaultRadius(),
      extraTransforms: initialExtraTransforms
    };

    return config;
  }

  /**
   * Get spherical position for an area element.
   * Uses explicit spherical coordinates if available, otherwise converts from percentage-based positioning.
   *
   * @param boundingBox - The bounding box to get position for.
   *
   * @returns Spherical coordinates (yaw, pitch in radians).
   */
  private getBoundingBoxSphericalPosition(boundingBox: BoundingBox): ISphericalCoords {
    // Convert percentage-based positioning to spherical coordinates
    // This matches the logic from the original area-hotspot directive
    let yaw = (boundingBox.left / 100) * 2 * Math.PI - Math.PI;
    let pitch = (0.5 - boundingBox.top / 100) * Math.PI;

    if (this.isNew && this.scene) {
      // Get current camera position
      const cameraState = this.scene.getCurrentCameraState();

      // Update bounding box with camera position
      yaw = cameraState.yaw;
      pitch = cameraState.pitch;
    }

    // Use explicit spherical coordinates if available
    if (boundingBox.spherical && !this.isNew) {
      yaw = boundingBox.spherical.yaw;
      pitch = boundingBox.spherical.pitch;
    }

    return { yaw, pitch };
  }

  /**
   * Update the bounding box with new spherical coordinates and emit the change.
   * This is called when isNew is true to update the area element with the camera position.
   *
   * @param yaw - New yaw position in radians.
   * @param pitch - New pitch position in radians.
   */
  private updateBoundingBoxWithNewPosition(yaw: number, pitch: number): void {
    if (!this.boundingBox || !this.scene) {
      return;
    }

    try {
      // Update the bounding box spherical coordinates
      if (!this.boundingBox.spherical) {
        this.boundingBox.spherical = {
          yaw: 0,
          pitch: 0,
          distance: this.scene.calculateDefaultRadius()
        };
      }

      this.boundingBox.spherical.yaw = yaw;
      this.boundingBox.spherical.pitch = pitch;

      // Convert spherical coordinates back to percentage-based positioning for consistency
      const left = ((yaw + Math.PI) / (2 * Math.PI)) * 100;
      const top = (0.5 - pitch / Math.PI) * 100;

      this.boundingBox.left = left;
      this.boundingBox.top = top;

      // Emit the bounding box change event
      const boundingBoxEvent: IBoundingBoxEvent = {
        hotspotId: this.hotspotId,
        boundingBox: { ...this.boundingBox }
      };

      this.boundingBoxChanged.emit(boundingBoxEvent);
    } catch (error) {
      this.logger.error('Failed to update bounding box with new position', error as Error);
    }
  }

  /**
   * Animate camera to focus on this hotspot.
   * Triggers when enableCameraAnimation is true, regardless of edit mode.
   */
  private async animateCameraToHotspot(): Promise<void> {
    if (!this.scene || !this.boundingBox || !this.enableCameraAnimation) {
      return;
    }

    try {
      const position = this.getBoundingBoxSphericalPosition(this.boundingBox);
      const cameraState = this.scene.getCurrentCameraState();

      const viewState = { yaw: position.yaw, pitch: position.pitch, fov: cameraState.fov };

      await this.scene.animateToViewState(viewState, this.cameraAnimationDuration);

      this.cameraAnimated.emit(viewState);
    } catch (error) {
      this.logger.error('Camera animation failed', error as Error);
    }
  }

  /**
   * Update layer assignment for all hotspots when zIndex changes.
   * This is a simplified approach that directly updates the layer assignments.
   */
  private updateLayerAssignment(): void {
    if (!this.scene || !this.scene.layerManager || !this.sceneHotspotContainer?.hotspot) {
      this.logger.warn(`Cannot update layer assignment for hotspot ${this.hotspotId}: scene not available`);
      return;
    }

    try {
      // Re-assign main hotspot to base layer
      this.scene.layerManager.assignHotspotToLayer(this.sceneHotspotContainer.hotspot, MarzipanoLayerIndex.BASE_HOTSPOTS, this.zIndex);

      // Re-assign drag overlay if it exists
      if (this.sceneHotspotContainer.dragOverlayHotspot) {
        this.scene.layerManager.assignHotspotToLayer(this.sceneHotspotContainer.dragOverlayHotspot, MarzipanoLayerIndex.DRAG_OVERLAYS, this.zIndex);
      }

      // Re-assign transform overlay if it exists
      if (this.sceneHotspotContainer.transformOverlayHotspot) {
        this.scene.layerManager.assignHotspotToLayer(this.sceneHotspotContainer.transformOverlayHotspot, MarzipanoLayerIndex.TRANSFORM_OVERLAYS, this.zIndex);
      }
    } catch (error) {
      this.logger.error(`Failed to update layer assignment for hotspot ${this.hotspotId}`, error as Error);
    }
  }

  /**
   * Apply proper sizing to the hotspot element based on bounding box percentages
   * and parent container dimensions.
   */
  private applyBasicSizing(): void {
    if (!this.parentContainer || !this.boundingBox) {
      return;
    }

    const containerElement = this.parentContainer.nativeElement;
    const boundingBox = this.boundingBox;

    // Get parent container dimensions
    const containerWidth = containerElement.offsetWidth;
    const containerHeight = containerElement.offsetHeight;

    // Prefer fixed reference size if provided (ensures consistent matrix across contexts)
    const refSize = boundingBox.perspective?.referenceSize;

    if (refSize && refSize.width > 0 && refSize.height > 0) {
      this.element.style.width = `${refSize.width}px`;
      this.element.style.height = `${refSize.height}px`;
      return;
    }

    // Fallback: calculate actual pixel dimensions from percentages
    const pixelWidth = (boundingBox.width / 100) * containerWidth;
    const pixelHeight = (boundingBox.height / 100) * containerHeight;

    // Apply dimensions to the element
    this.element.style.width = `${pixelWidth}px`;
    this.element.style.height = `${pixelHeight}px`;
  }
}
